import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#050505] text-[#D4D4D8] pb-24">
      <header className="sticky top-0 z-30 bg-[#050505]/95 backdrop-blur-sm border-b border-white/5 px-4 h-12 flex items-center gap-3">
        <Link to="/" className="text-white" data-testid="back-to-home">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="font-bold text-white font-['Unbounded'] text-sm">Privacy Policy</h1>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 text-sm leading-relaxed space-y-5">
        <p className="text-xs text-[#71717A]">Last updated: 14 February 2026</p>

        <Section title="Who we are">
          <p>
            Animus ("we", "us", "our") is an AI character chat platform operated by the Animus team.
            We can be reached at <a href="mailto:support@animusapp.ai" className="text-[#E11D48]">support@animusapp.ai</a>.
          </p>
        </Section>

        <Section title="Information we collect">
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Account data</b>: email, display name, hashed password (or Google profile if you sign in with Google).</li>
            <li><b>Profile data</b>: avatar, bio, personas you create, characters you upload.</li>
            <li><b>Chat data</b>: messages you send to AI characters and the AI responses.</li>
            <li><b>Usage data</b>: IP address, device type, app interactions, login timestamps.</li>
            <li><b>Payment data</b>: handled by Stripe — we never see your card details. We store transaction IDs only.</li>
          </ul>
        </Section>

        <Section title="How we use it">
          <ul className="list-disc pl-5 space-y-1">
            <li>To run your account and provide the chat service.</li>
            <li>To process payments (via Stripe) and subscriptions.</li>
            <li>To moderate uploaded images and characters for safety.</li>
            <li>To send transactional emails (password resets, receipts).</li>
            <li>To detect abuse, fraud, and protect the service.</li>
          </ul>
        </Section>

        <Section title="Who we share it with">
          <ul className="list-disc pl-5 space-y-1">
            <li><b>OpenRouter</b> — chat messages are sent to OpenRouter (a third-party AI provider) to generate replies.</li>
            <li><b>OpenAI / Google</b> — for image moderation and avatar generation.</li>
            <li><b>Stripe</b> — for payment processing.</li>
            <li><b>Resend</b> — for sending transactional emails.</li>
            <li>We do <b>not</b> sell your personal data to anyone.</li>
          </ul>
        </Section>

        <Section title="Your rights">
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Access</b> — request a copy of your data.</li>
            <li><b>Correction</b> — edit your profile in Settings.</li>
            <li><b>Deletion</b> — delete your account in Settings → Account → "Delete my account". This wipes all your data permanently.</li>
            <li><b>Withdraw consent</b> — log out and stop using the service at any time.</li>
            <li>Email <a href="mailto:support@animusapp.ai" className="text-[#E11D48]">support@animusapp.ai</a> for any of the above.</li>
          </ul>
        </Section>

        <Section title="Age restriction">
          <p>
            Animus is strictly for users <b>18 years of age or older</b>. The service contains adult content
            and is not suitable for minors. By using Animus you confirm that you are 18+.
          </p>
        </Section>

        <Section title="Data retention">
          <p>
            We keep your data while your account is active. If you delete your account, all personal data and
            chat history is removed within 30 days. Anonymised analytics may be retained.
          </p>
        </Section>

        <Section title="Cookies">
          <p>
            We use first-party httpOnly cookies to keep you signed in. No third-party advertising trackers.
          </p>
        </Section>

        <Section title="Changes">
          <p>
            We'll post any updates to this page and update the "Last updated" date above.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions? Email <a href="mailto:support@animusapp.ai" className="text-[#E11D48]">support@animusapp.ai</a>.
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-1.5">
      <h2 className="text-white font-['Unbounded'] text-base">{title}</h2>
      <div className="text-[#A1A1AA] text-sm">{children}</div>
    </section>
  );
}
